import { useState, useEffect, ChangeEvent } from 'react';
import deepEqual from 'deep-equal';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import { constants, BaseDialog, PrimaryButton, notify } from '../index';
import type { EditPlanMemberDialogProps, PlanMemberFormState, PatientUpdateData } from './types';
import {
  OutlinedTextField,
  DatePickerField,
  TaxIdField,
  PhoneNumberField,
  ZipCodeField,
} from '../fields';

import { isEmpty, filterMatchingPairs, filterEmpty, formatDateForRequest } from '../utilities';

import {
  usePlanMemberForm,
  initialPlanMemberFormState,
  getMemberFormState,
  useUpdateMember,
} from './EmployeeUtils';
import TagsDropdown from './TagsDropdown';

export default function EditPlanMemberDialog({
  open,
  onClose,
  employee,
  member,
  portal,
  corpId,
  refresh,
}: EditPlanMemberDialogProps) {
  const [isDirty, setIsDirty] = useState(false);
  const [initialValues, setInitialValues] = useState<Partial<PlanMemberFormState>>(
    initialPlanMemberFormState
  );
  const { memberData, updateMember } = useUpdateMember({ employee, member, portal, corpId });
  const { memberData: employeeData } = useUpdateMember({
    employee,
    member: employee,
    portal,
    corpId,
  });

  const {
    formState,
    setFormState,
    resetFormState,
    clearErrors,
    errors,
    handleChange: originalHandleChange,
    validate,
  } = usePlanMemberForm({ specifyRelationship: true });

  const isPrimary = formState.relationship === constants.RELATIONSHIP_PRIMARY;

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIsDirty(true);
    originalHandleChange(e);
  };

  useEffect(() => {
    if (open && !isEmpty(memberData)) {
      const newInitialValues = getMemberFormState(memberData);
      resetFormState(newInitialValues);
      setInitialValues(newInitialValues);
      setIsDirty(false);
      clearErrors();
    }
  }, [open, memberData]);

  function canSubmit(submit: () => void) {
    const isValid = validate();
    if (isValid) {
      submit();
    }
  }

  function submitMember() {
    // get the values that changed since opening the dialog
    const filteredValues = filterMatchingPairs(formState, initialValues);

    // put only non-empty values in the payload
    const patientData: PatientUpdateData = {
      ...filterEmpty({
        first_name: filteredValues.firstName,
        middle_name: filteredValues.middleName,
        last_name: filteredValues.lastName,
        relationship: formState.relationship,
        email: filteredValues.email,
        phone_number: filteredValues.phone,
        identification_type: filteredValues.identificationType,
        identification_id: filteredValues.identificationId,
        dob: filteredValues.dob ? formatDateForRequest(filteredValues.dob) : null,
      }),
      member_tags: filteredValues.memberTags,
    };

    // Only add address if address has changed
    const formAddress = {
      address1: formState.address1,
      address2: formState.address2,
      city: formState.city,
      state: formState.state,
      zip: formState.zip,
    };

    if (!deepEqual(formAddress, memberData.address)) {
      patientData.address = formAddress;
    }

    return updateMember
      .mutateAsync({ patientData })
      .then(() => {
        notify('Member information updated successfully', 'success');
        refresh?.();
        onClose();
      })
      .catch((error) => notify(error.message, 'error'));
  }

  return (
    <BaseDialog
      open={open}
      onClose={() => {
        setIsDirty(false);
        onClose();
      }}
      dialogName="edit-employee-dialog"
      showCloseButton
      maxWidth="md"
      title={isPrimary ? 'Edit Employee' : 'Edit Member'}
    >
      <Box mb={2}>
        <Divider />
      </Box>
      <Box margin="0 auto" width="fit-content">
        <Stack paddingX={2} maxWidth={600}>
          <Typography variant="h3" color="primary" mb={2}>
            {isPrimary ? 'Employee' : 'Member'} information
          </Typography>
          <Stack direction="row">
            <OutlinedTextField
              select
              id="relationship-type"
              name="relationship"
              label="Relationship"
              value={formState.relationship ?? ''}
              onChange={handleChange}
              error={!!errors.relationship}
              helperText={errors.relationship}
              disabled={isPrimary}
            >
              {!isPrimary && (
                <MenuItem key="relationship-spouse" value={constants.RELATIONSHIP_SPOUSE}>
                  {constants.RELATIONSHIP_LABEL_SPOUSE}
                </MenuItem>
              )}
              {!isPrimary && (
                <MenuItem key="relationship-dependent" value={constants.RELATIONSHIP_DEPENDENT}>
                  {constants.RELATIONSHIP_LABEL_DEPENDENT}
                </MenuItem>
              )}
              {!isPrimary && (
                <MenuItem
                  key="relationship-dependent-adult"
                  value={constants.RELATIONSHIP_DEPENDENT_ADULT}
                >
                  {constants.RELATIONSHIP_LABEL_DEPENDENT_ADULT}
                </MenuItem>
              )}
              {!isPrimary && (
                <MenuItem
                  key="relationship-dependent-student"
                  value={constants.RELATIONSHIP_DEPENDENT_STUDENT}
                >
                  {constants.RELATIONSHIP_LABEL_DEPENDENT_STUDENT}
                </MenuItem>
              )}
              {isPrimary && (
                <MenuItem key="relationship-primary" value={constants.RELATIONSHIP_PRIMARY}>
                  {constants.RELATIONSHIP_LABEL_PRIMARY}
                </MenuItem>
              )}
            </OutlinedTextField>
            <DatePickerField
              id="employee-dob"
              name="dob"
              label="Date of Birth"
              onChange={handleChange}
              value={formState.dob}
              helperText={errors.dob}
              error={!!errors.dob}
            />
          </Stack>
          <Stack direction="row">
            <OutlinedTextField
              id="employee-first-name"
              name="identificationType"
              label="Identification Type"
              onChange={handleChange}
              value={formState.identificationType}
              helperText={errors.identificationType}
              error={!!errors.identificationType}
              disabled
              select
            >
              {constants.TAX_ID_MEMBER_OPTIONS.map(({ label, value }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </OutlinedTextField>
            <TaxIdField
              id="employee-ssn"
              name="identificationId"
              label="Identificaton #"
              onChange={handleChange}
              value={formState.identificationId}
              // Note that ssn field is now masked. Masked
              // fields don't display correctly in a TaxIdField
              // so force them to be text field by passing the type
              // as constants.TAX_ID_TYPE_MEMBER_NONE
              type={
                formState.identificationType === constants.TAX_ID_TYPE_MEMBER_SSN
                  ? constants.TAX_ID_TYPE_MEMBER_NONE
                  : formState.identificationType
              }
              helperText={errors.identificationId}
              error={!!errors.identificationId}
              // don't allow verified SSNs or Bento assigned member IDs to be edited
              disabled={formState.identificationType === constants.TAX_ID_TYPE_MEMBER_NONE}
            />
          </Stack>
          <Stack direction="row">
            <OutlinedTextField
              id="employee-first-name"
              name="firstName"
              label="First Name"
              onChange={handleChange}
              value={formState.firstName}
              helperText={errors.firstName}
              error={!!errors.firstName}
            />
            <OutlinedTextField
              id="employee-middle-name"
              name="middleName"
              label="Middle Name"
              onChange={handleChange}
              value={formState.middleName}
              helperText={errors.middleName}
              error={!!errors.middleName}
            />
            <OutlinedTextField
              id="employee-last-name"
              name="lastName"
              label="Last Name"
              onChange={handleChange}
              value={formState.lastName}
              helperText={errors.lastName}
              error={!!errors.lastName}
            />
          </Stack>
          <PhoneNumberField
            id="employee-phone"
            name="phone"
            label="Phone"
            onChange={handleChange}
            value={formState.phone}
            helperText={errors.phone}
            error={!!errors.phone}
          />
          <OutlinedTextField
            id="employee-email"
            name="email"
            label="Email"
            onChange={handleChange}
            value={formState.email}
            helperText={errors.email}
            error={!!errors.email}
          />
          <TagsDropdown
            formState={formState}
            setFormState={setFormState}
            setIsDirty={setIsDirty}
            initialTags={formState.memberTags}
          />
          {isPrimary && <Box sx={{ height: 1.5 }} />}
          {!isPrimary && (
            <Button
              variant="outlined"
              color="primary"
              id="btn-copy-tags"
              sx={{ marginBottom: 2 }}
              onClick={() => {
                setIsDirty(true);
                setFormState({ ...formState, memberTags: employeeData?.member_tags });
              }}
              disabled={!employeeData?.member_tags}
            >
              Copy division codes from primary member
            </Button>
          )}
          <OutlinedTextField
            id="employee-address1"
            name="address1"
            label="Address 1"
            onChange={handleChange}
            value={formState.address1}
            helperText={errors.address1}
            error={!!errors.address1}
          />
          <OutlinedTextField
            id="employee-address2"
            name="address2"
            label="Address 2"
            onChange={handleChange}
            value={formState.address2}
            helperText={errors.address2}
            error={!!errors.address2}
          />
          <Stack direction="row">
            <OutlinedTextField
              id="employee-address1"
              name="city"
              label="City"
              onChange={handleChange}
              value={formState.city}
              helperText={errors.city}
              error={!!errors.city}
            />
            <OutlinedTextField
              select
              label="State"
              id="state"
              name="state"
              value={formState.state}
              onChange={handleChange}
              error={!!errors.state}
              helperText={errors.state}
            >
              {Object.entries(constants.US_STATES).map(([stateAbbrev, state]) => (
                <MenuItem key={stateAbbrev} value={stateAbbrev}>
                  {state}
                </MenuItem>
              ))}
            </OutlinedTextField>
            <ZipCodeField
              id="employee-address2"
              name="zip"
              label="Zip code"
              onChange={handleChange}
              value={formState.zip}
              helperText={errors.zip}
              error={!!errors.zip}
            />
          </Stack>
          <Stack direction="row" mt={2}>
            <PrimaryButton
              id="update-member-btn"
              onClick={() => {
                canSubmit(submitMember);
              }}
              disabled={!isDirty || updateMember.isLoading}
            >
              Update
            </PrimaryButton>
            <Button
              onClick={() => {
                setIsDirty(false);
                onClose();
              }}
            >
              Cancel
            </Button>
          </Stack>
        </Stack>
      </Box>
    </BaseDialog>
  );
}
